import React, { useState, useEffect, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import axios from 'axios';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

// ─── Inline icons ────────────────────────────────────────────────────────────
const AmbulanceIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="9" width="22" height="11" rx="2"/><path d="M16 9V5l3 4"/>
    <path d="M8 9V5l-3 4"/><line x1="12" y1="9" x2="12" y2="5"/>
    <path d="M3 13h18"/><circle cx="7" cy="20" r="2"/><circle cx="17" cy="20" r="2"/>
    <path d="M11 13v-2h2v2"/>
  </svg>
);
const NursingIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const PhysioIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="5" r="2"/><path d="M12 7v5l-3 3"/><path d="M9 12l3 3 3-3"/><path d="M9 17v4"/><path d="M15 17v4"/>
  </svg>
);
const EquipmentIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);
const EmergencyIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);
const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const WalletIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/>
    <path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 0 0 0 4h4v-4z"/>
  </svg>
);
const CardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
    <line x1="1" y1="10" x2="23" y2="10"/>
  </svg>
);

// ─── Constants ────────────────────────────────────────────────────────────────
const BENEFITS = [
  { icon: AmbulanceIcon, label: 'Quick Ambulance Support', color: 'text-red-500' },
  { icon: NursingIcon,   label: 'Home Nursing Assistance', color: 'text-blue-500' },
  { icon: PhysioIcon,    label: 'Physiotherapy at Home',   color: 'text-green-500' },
  { icon: EquipmentIcon, label: 'Medical Equipment Rental', color: 'text-[#004276]' },
  { icon: EmergencyIcon, label: 'Emergency Guidance',       color: 'text-orange-500' },
];

const WHATSAPP_NUM = process.env.REACT_APP_WHATSAPP_DCS?.trim() || '7205902499';
const CARE_SATHI   = process.env.REACT_APP_CARE_SATHI;
const BASE_URL     = process.env.REACT_APP_BASEURL;

// ─── Plan Card ────────────────────────────────────────────────────────────────
function PlanCard({ plan, isSelected, isCurrent, onClick }) {
  const isYearly     = plan.planName?.toLowerCase().includes('yearly') && !plan.planName?.toLowerCase().includes('half');
  const isHalfYearly = plan.planName?.toLowerCase().includes('half') || plan.durationMonth === 6;
  const discount     = plan.discountAmount && plan.amount
    ? Math.round((plan.discountAmount / plan.amount) * 100) : null;

  return (
    <div
      onClick={onClick}
      className={`
        relative rounded-2xl border-2 p-5 cursor-pointer transition-all duration-200 select-none
        ${isSelected
          ? 'border-[#00BD7F] bg-gradient-to-br from-[#f0fdf9] to-[#e0f7fa] shadow-lg scale-[1.02]'
          : 'border-gray-200 bg-white hover:border-[#00BD7F]/50 hover:shadow-md'
        }
      `}
    >
      {/* Badge */}
      {isYearly && (
        <span className="absolute top-3 right-3 bg-[#00BD7F] text-white text-[10px] font-bold px-2.5 py-1 rounded-full">
          Best Value
        </span>
      )}
      {isHalfYearly && discount && (
        <span className="absolute top-3 right-3 bg-[#00B5E2] text-white text-[10px] font-bold px-2.5 py-1 rounded-full">
          {discount}% Off
        </span>
      )}
      {isCurrent && (
        <span className="absolute top-3 left-3 bg-[#10B981] text-white text-[10px] font-bold px-2.5 py-1 rounded-full">
          Current
        </span>
      )}

      {/* Radio indicator */}
      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mb-3 transition-colors
        ${isSelected ? 'border-[#00BD7F] bg-[#00BD7F]' : 'border-gray-300'}`}>
        {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
      </div>

      <h3 className="text-base font-bold text-[#101010] mb-1 pr-16">{plan.planName}</h3>
      <p className="text-2xl font-extrabold text-[#101010]">
        ₹{plan.payableAmount}
        {plan.amount && plan.amount !== plan.payableAmount && (
          <span className="text-sm font-normal text-gray-400 line-through ml-2">₹{plan.amount}</span>
        )}
      </p>
      <p className="text-sm text-gray-500 mt-0.5">
        for {plan.durationMonth} {plan.durationMonth === 1 ? 'month' : 'months'}
      </p>
      {plan.description && (
        <p className="mt-2 text-xs font-semibold text-[#00B5E2]">{plan.description}</p>
      )}
    </div>
  );
}

// ─── Current Active Plan ───────────────────────────────────────────────────────
function ActivePlanCard({ plan }) {
  const planName      = plan?.plan_name || plan?.planName || 'N/A';
  const startDate     = plan?.start_date || plan?.startDate;
  const endDate       = plan?.end_date || plan?.endDate;
  const daysRemaining = plan?.days_remaining || 0;
  const formatted     = (d) => d ? moment(d).format('DD/MM/YYYY') : '—';

  return (
    <div className="bg-gradient-to-r from-[#f0fdf9] to-[#e0f7fa] border border-[#00BD7F]/30 rounded-2xl p-4">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <h3 className="text-base font-bold text-[#101010]">{planName}</h3>
        <span className="bg-[#10B981] text-white text-[10px] font-bold px-2.5 py-1 rounded-full">
          {plan?.status_label || 'Active'}
        </span>
        {daysRemaining >= 0 && (
          <span className="bg-[#FF9800] text-white text-[10px] font-bold px-2.5 py-1 rounded-full">
            {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} left
          </span>
        )}
      </div>
      <p className="text-sm text-gray-600">
        {formatted(startDate)} – {formatted(endDate)}
      </p>
    </div>
  );
}

// ─── Main SubscriptionDialog ──────────────────────────────────────────────────
const SubscriptionDialog = ({ visible, onHide, userData }) => {
  const navigate = useNavigate();

  // Plan data
  const [plans, setPlans]               = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [currentActivePlans, setCurrentActivePlans] = useState([]);
  const [upcomingCount, setUpcomingCount] = useState(0);
  const [latestSubscription, setLatestSubscription] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(false);

  // Payment modal
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan]         = useState(null);
  const [payMode, setPayMode]                   = useState('digital'); // 'digital' | 'wallet'
  const [walletBalance, setWalletBalance]       = useState(null);
  const [referralCode, setReferralCode]         = useState('');
  const [referralError, setReferralError]       = useState('');
  const [loadingPayment, setLoadingPayment]     = useState(false);
  const [paymentError, setPaymentError]         = useState('');
  const [showNotice, setShowNotice]             = useState(true);

  const patientCode = userData?.code;

  // ── Fetch plans ──────────────────────────────────────────────────────────────
  const fetchPlans = useCallback(async () => {
    if (!visible) return;
    setLoadingPlans(true);
    try {
      const url = patientCode
        ? `${CARE_SATHI}ds/subscription/plans?patientId=${patientCode}`
        : `${CARE_SATHI}ds/subscription/plans`;
      const res  = await fetch(url);
      const data = await res.json();

      if (data?.status === 200 && data?.success) {
        const mapped = (data?.data?.plans || []).map((p) => ({
          id:                   p.id,
          subscriptionId:       p.subscription_id,
          planName:             p.plan_name,
          durationMonth:        p.duration_months,
          payableAmount:        p.payable_amount,
          amount:               p.amount,
          discountAmount:       p.discount_amount,
          description:          p.description,
          status:               p.status,
          isVisible:            p.is_visible,
          currentActivePlan:    p.currentActivePlan,
          isLatestSubscription: p.isLatestSubscription,
        }));

        const filtered = mapped.filter((p) => p.planName !== 'Monthly Plan');
        setPlans(filtered);

        // Auto-select best plan
        if (filtered.length > 0 && !selectedPlanId) {
          const yearly = filtered.find((p) =>
            (p.planName?.toLowerCase().includes('yearly') && !p.planName?.toLowerCase().includes('half')) ||
            p.durationMonth === 12
          );
          const auto = yearly || filtered[0];
          setSelectedPlanId(auto.id || auto.subscriptionId);
        }
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
    } finally {
      setLoadingPlans(false);
    }
  }, [visible, patientCode]);

  // ── Fetch subscription status ─────────────────────────────────────────────────
  const fetchSubscriptionStatus = useCallback(async () => {
    if (!patientCode || !visible) return;
    try {
      const res  = await axios.get(`${CARE_SATHI}ds/subscription/patient/${patientCode}/status?status=1`);
      const subs = res?.data?.data?.subscriptions || res?.data?.data || [];
      const active = Array.isArray(subs) ? subs.filter((s) => s.status === 1) : [];
      setCurrentActivePlans(active);
    } catch (err) {
      console.error('Error fetching subscription status:', err);
    }
  }, [patientCode, visible]);

  // ── Fetch upcoming count ──────────────────────────────────────────────────────
  const fetchUpcomingCount = useCallback(async () => {
    if (!patientCode || !visible) return;
    try {
      const res = await axios.get(`${CARE_SATHI}ds/subscription/patient/${patientCode}/status?status=2`);
      if (res?.data?.success && res?.data?.data?.summary) {
        setUpcomingCount(res.data.data.summary.upcoming_count || 0);
      }
      if (res?.data?.data?.latestSubscription) {
        setLatestSubscription(res.data.data.latestSubscription);
      }
    } catch (err) {
      console.error('Error fetching upcoming:', err);
    }
  }, [patientCode, visible]);

  // ── Fetch wallet balance ──────────────────────────────────────────────────────
  const fetchWalletBalance = useCallback(async () => {
    if (!patientCode || !visible) return;
    try {
      const res = await axios.get(`${BASE_URL}api/v1/payments/wallet/${patientCode}`);
      const bal = res?.data?.data?.balance ?? res?.data?.balance ?? null;
      setWalletBalance(bal);
    } catch {
      setWalletBalance(null);
    }
  }, [patientCode, visible]);

  useEffect(() => {
    if (visible) {
      fetchPlans();
      fetchSubscriptionStatus();
      fetchUpcomingCount();
      fetchWalletBalance();
    }
  }, [visible]);

  // ── Computed ──────────────────────────────────────────────────────────────────
  const activePlan   = plans.find((p) => p.id === selectedPlanId || p.subscriptionId === selectedPlanId);
  const isRenewPlan  = activePlan?.isLatestSubscription === true;
  const currentPlanNames = currentActivePlans.map((p) => p?.plan_name || p?.planName);

  // ── Handle "Subscribe Now" click ──────────────────────────────────────────────
  const handleSubscribeNow = async () => {
    setPaymentError('');
    if (upcomingCount >= 3) {
      setPaymentError('Upcoming subscription limit reached.');
      return;
    }
    if (!selectedPlanId) {
      setPaymentError('Please select a plan first.');
      return;
    }

    const plan = plans.find((p) => p.id === selectedPlanId || p.subscriptionId === selectedPlanId);
    if (!plan) return;

    // Check half-yearly restriction
    const planNameLower = (plan.planName || '').toLowerCase();
    const isHalfYearly = planNameLower.includes('half') || plan.durationMonth === 6;
    if (isHalfYearly && latestSubscription === 'SUB003') {
      setPaymentError('Your subscription is on the yearly plan. Renewal is available for the yearly plan only.');
      return;
    }

    setSelectedPlan(plan);
    setPayMode('digital');
    setReferralCode('');
    setReferralError('');
    setPaymentModalOpen(true);
  };

  // ── Handle Pay Now ────────────────────────────────────────────────────────────
  const handlePayNow = async () => {
    setPaymentError('');
    setLoadingPayment(true);

    const startDate  = moment();
    const expiry     = moment().add(selectedPlan?.durationMonth || 12, 'months');
    const fmtStart   = startDate.format('YYYY-MM-DD');
    const fmtExpiry  = expiry.format('YYYY-MM-DD');

    try {
      if (payMode === 'wallet') {
        // ── Wallet payment ──
        if (walletBalance === null || walletBalance <= 0) {
          setPaymentError('Insufficient wallet balance.');
          setLoadingPayment(false);
          return;
        }
        if (selectedPlan?.payableAmount > walletBalance) {
          setPaymentError('Wallet balance is not sufficient.');
          setLoadingPayment(false);
          return;
        }

        const txnRes = await axios.put(`${BASE_URL}api/v1/payments/wallet/transaction`, {
          patientCode: patientCode,
          amount:      selectedPlan?.payableAmount,
        });

        if (txnRes?.data?.statusCode === 200 || txnRes?.data?.data?.transcationId) {
          const txnId = txnRes?.data?.data?.transcationId || '';
          // Store for thank-you page
          localStorage.setItem('redirectUrl',       '/thankyoucaresathi');
          localStorage.setItem('patientCode',       JSON.stringify(patientCode));
          localStorage.setItem('sourceChannel',     'B2C');
          localStorage.setItem('totalamount',       selectedPlan?.payableAmount);
          localStorage.setItem('duration',          selectedPlan?.durationMonth);
          localStorage.setItem('trancationid',      txnId);
          localStorage.setItem('payMode',           'CureBay wallet');
          localStorage.setItem('walletstatus',      'success');
          localStorage.setItem('email',             userData?.email || '');
          localStorage.setItem('mobile',            userData?.mobile || '');
          localStorage.setItem('startdate',         fmtStart);
          localStorage.setItem('expiry',            fmtExpiry);
          localStorage.setItem('subscriptionName',  selectedPlan?.planName);
          localStorage.setItem('subscriptionId',    selectedPlan?.subscriptionId);
          localStorage.setItem('paymentRemarks',    'Your order placed successfully');
          if (referralCode.trim()) localStorage.setItem('referralCode', referralCode.trim());
          onHide();
          navigate('/thankyoucaresathi');
        } else {
          setPaymentError('Wallet payment failed. Please try again.');
        }

      } else {
        // ── Digital payment ──
        const payload = {
          patientId:              patientCode,
          subscriptionId:         selectedPlan?.subscriptionId,
          planName:               selectedPlan?.planName,
          payMode:                'Digital Payment',
          sourceChannel:          'B2C',
          whatsAppNum:            userData?.mobile || '',
          isSubscriptionRenewal:  isRenewPlan ? 'true' : 'false',
        };

        const res = await axios.post(
          `${BASE_URL}PatientSubscription/patient-caresathi-subscription`,
          payload
        );

        const html = res.data;
        if (typeof html === 'string' && html.includes('<')) {
          // Store payment context
          localStorage.setItem('duration',         selectedPlan?.durationMonth);
          localStorage.setItem('startdate',        fmtStart);
          localStorage.setItem('expiry',           fmtExpiry);
          localStorage.setItem('totalamount',      selectedPlan?.payableAmount);
          localStorage.setItem('subscriptionName', selectedPlan?.planName);
          localStorage.setItem('subscriptionId',   selectedPlan?.subscriptionId);
          localStorage.setItem('patientCode',      patientCode);
          localStorage.setItem('payMode',          'Digital Payment');
          localStorage.setItem('sourceChannel',    'B2C');
          localStorage.setItem('email',            userData?.email || '');
          localStorage.setItem('mobile',           userData?.mobile || '');
          if (referralCode.trim()) localStorage.setItem('referralCode', referralCode.trim());

          const payWin = window.open('', '_self');
          payWin.document.open();
          payWin.document.write(html);
          payWin.document.close();
        } else {
          setPaymentError('Failed to initiate payment. Please try again.');
        }
      }
    } catch (err) {
  console.error('Payment error:', err);
  setPaymentError(err?.response?.data?.message || 'Something went wrong. Please try again.');
  setLoadingPayment(false); // only stop loading on error
}
  };

  // ── Dialog header ─────────────────────────────────────────────────────────────
  const mainHeader = (
    <div className="flex items-center gap-3 py-1">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00BD7F] to-[#00B5E2] flex items-center justify-center">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="1.5">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
      </div>
      <div>
        <h2 className="text-lg font-bold text-[#101010] m-0 leading-tight">Choose Your CareSathi Plan</h2>
        <p className="text-xs text-gray-500 m-0">24×7 personal healthcare companion</p>
      </div>
    </div>
  );

  const paymentHeader = (
    <div className="flex items-center gap-2 py-1">
      <CardIcon />
      <h2 className="text-base font-bold text-[#101010] m-0">Complete Your Payment</h2>
    </div>
  );

  return (
    <>
      {/* ── Main Subscription Dialog ─────────────────────────────────────────── */}
      <Dialog
        visible={visible}
        onHide={onHide}
        header={mainHeader}
        position="top"
        draggable={false}
        resizable={false}
        className="w-full max-w-2xl"
        style={{ width: 'min(96vw, 680px)' }}
        contentStyle={{ padding: '0 1.5rem 1.5rem', maxHeight: '80vh', overflowY: 'auto' }}
      >
        <div className="flex flex-col gap-6">

          {/* Benefits strip */}
          <div className="bg-gradient-to-r from-[#f0fdf9] to-[#e8f8fe] rounded-2xl p-4">
            <h3 className="text-sm font-bold text-[#101010] mb-3">What You Get with CareSathi</h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {BENEFITS.map(({ icon: Icon, label, color }) => (
                <div key={label} className="flex flex-row sm:flex-col items-center gap-2 sm:gap-1 sm:text-center">
                  <div className={`w-9 h-9 rounded-full bg-white shadow-sm flex items-center justify-center flex-shrink-0 ${color}`}>
                    <Icon />
                  </div>
                  <p className="text-[11px] text-[#101010] font-medium m-0 leading-tight">{label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Service notice */}
          {showNotice && (
            <div className="flex items-start gap-3 bg-[#FFF8EC] border border-[#FBBF24]/40 rounded-xl p-3 relative">
              <svg className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
              </svg>
              <p className="text-xs text-[#78350F] flex-1 m-0 leading-relaxed">
               Availability of services may be limited outside the geographical boundaries of Odisha, Chhasttisgarh, and Jharkhand.
            We will soon be in your City.
            For assistance and confirmation regarding service delivery in your area,
            please connect with our customer care @ 
                <button
                  className="text-[#004171] underline font-semibold"
                  onClick={() => {
                    const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
                    if (isMobile) window.location.href = 'tel:8335000999';
                    else { navigator.clipboard.writeText('8335000999'); }
                  }}
                >
                  8335000999
                </button>
              </p>
              <button onClick={() => setShowNotice(false)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                <CloseIcon />
              </button>
            </div>
          )}

          {/* Current active plan(s) */}
          {currentActivePlans.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-[#101010] mb-2">Your Current Plan</h3>
              <div className="flex flex-col gap-2">
                {currentActivePlans.map((plan, i) => (
                  <ActivePlanCard key={plan?.id || plan?.subscription_id || i} plan={plan} />
                ))}
              </div>
            </div>
          )}

          {/* Plans */}
          {loadingPlans ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-8 h-8 rounded-full border-3 border-[#00BD7F] border-t-transparent" style={{ borderWidth: 3 }} />
              <span className="ml-3 text-sm text-gray-500">Loading plans…</span>
            </div>
          ) : plans.length > 0 ? (
            <div>
              <h3 className="text-sm font-bold text-[#101010] mb-3">
                {currentActivePlans.length > 0 ? 'Renew / Upgrade Plan' : 'Available Plans'}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {plans.map((plan) => (
                  <PlanCard
                    key={plan.id || plan.subscriptionId}
                    plan={plan}
                    isSelected={selectedPlanId === (plan.id || plan.subscriptionId)}
                    isCurrent={currentPlanNames.includes(plan.planName)}
                    onClick={() => {
                      setSelectedPlanId(plan.id || plan.subscriptionId);
                      setPaymentError('');
                    }}
                  />
                ))}
              </div>
            </div>
          ) : null}

          {/* Upcoming limit warning */}
          {upcomingCount >= 2 && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3">
              You have reached the upcoming subscription limit. You cannot add more plans at this time.
            </div>
          )}

          {/* Error message */}
          {paymentError && (
            <p className="text-red-600 text-sm font-medium text-center">{paymentError}</p>
          )}

          {/* CTA */}
          {plans.length > 0 && upcomingCount < 2 && (
            <button
              onClick={handleSubscribeNow}
              className="w-full py-3.5 rounded-xl font-bold text-base text-white transition-all duration-200
                bg-gradient-to-r from-[#00BD7F] to-[#00B5E2] hover:opacity-90 hover:shadow-lg active:scale-[0.98] shadow-md"
            >
              {isRenewPlan ? 'Renew Now' : 'Subscribe Now'}
            </button>
          )}

          {/* WhatsApp CTA */}
          <a
            href={`https://wa.me/91${WHATSAPP_NUM}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 text-sm text-[#25D366] font-medium hover:underline"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#25D366">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Need help? Chat with us on WhatsApp
          </a>
        </div>
      </Dialog>

      {/* ── Payment Modal ─────────────────────────────────────────────────────── */}
      <Dialog
        visible={paymentModalOpen}
        onHide={() => { setPaymentModalOpen(false); setPaymentError(''); }}
        header={paymentHeader}
        position="top"
        draggable={false}
        resizable={false}
        style={{ width: 'min(96vw, 520px)', zIndex: 2000 }}
        contentStyle={{ padding: '0 1.5rem 1.5rem' }}
      >
        <div className="flex flex-col gap-5">

          {/* Plan summary */}
          <div className="bg-gradient-to-r from-[#f0fdf9] to-[#e8f8fe] rounded-xl p-4 flex gap-4 items-center">
            <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center shadow flex-shrink-0">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="url(#g)" strokeWidth="1.8">
                <defs>
                  <linearGradient id="g" x1="0" y1="0" x2="24" y2="24">
                    <stop offset="0%" stopColor="#00BD7F"/>
                    <stop offset="100%" stopColor="#00B5E2"/>
                  </linearGradient>
                </defs>
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-bold text-[#004171] m-0">CareSathi — {selectedPlan?.planName}</h3>
              <p className="text-xs text-gray-500 m-0 mt-0.5">
                Validity: {selectedPlan?.durationMonth} {selectedPlan?.durationMonth === 1 ? 'month' : 'months'}
              </p>
              <div className="flex flex-wrap gap-1 mt-1">
                {['Ambulance', 'Nursing', 'Equipment', 'Physio', 'Emergency'].map((b) => (
                  <span key={b} className="text-[10px] bg-white border border-[#00BD7F]/30 text-[#004171] px-2 py-0.5 rounded-full">
                    {b}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Billing details */}
          <div className="border border-gray-100 rounded-xl p-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Billing Details</h4>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Plan Amount</span>
              <span className="font-semibold">₹{selectedPlan?.payableAmount || 0}</span>
            </div>
            <div className="flex justify-between text-sm font-bold pt-2 border-t border-gray-100">
              <span>Total Payable</span>
              <span className="text-[#00BD7F]">₹{selectedPlan?.payableAmount || 0}</span>
            </div>
            {isRenewPlan && (
              <p className="text-[11px] text-gray-500 italic mt-2">
                Renewal will be activated once your current plan ends.
              </p>
            )}
          </div>

          {/* Payment mode */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Payment Mode</h4>
            <div className="flex flex-col gap-2">
              {/* Digital */}
              <label
                className={`flex items-center gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition-all
                  ${payMode === 'digital' ? 'border-[#00BD7F] bg-[#f0fdf9]' : 'border-gray-200 hover:border-gray-300'}`}
              >
                <input
                  type="radio"
                  name="payMode"
                  checked={payMode === 'digital'}
                  onChange={() => setPayMode('digital')}
                  className="accent-[#00BD7F]"
                />
                <div className="flex items-center gap-2">
                  <CardIcon />
                  <div>
                    <span className="text-sm font-medium text-[#101010]">Digital Payment</span>
                    <span className="block text-xs text-gray-400">Debit/Credit Card, UPI & more</span>
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Referral code */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Referral Code <span className="font-normal normal-case">(optional)</span>
            </label>
            <input
              type="text"
              value={referralCode}
              onChange={(e) => {
                setReferralCode(e.target.value.replace(/\./g, '').toUpperCase());
                setReferralError('');
              }}
              placeholder="Enter referral code"
              className={`w-full px-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 transition-all
                ${referralError ? 'border-red-400 focus:ring-red-200' : 'border-gray-200 focus:ring-[#00BD7F]/30 focus:border-[#00BD7F]'}`}
            />
            {referralError && <p className="text-red-500 text-xs mt-1">{referralError}</p>}
          </div>

          {/* Error */}
          {paymentError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">
              {paymentError}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 pt-1 border-t border-gray-100">
            <button
              onClick={() => { setPaymentModalOpen(false); setPaymentError(''); }}
              className="flex-1 py-3 rounded-xl border-2 border-[#00BD7F] text-[#00BD7F] font-semibold text-sm hover:bg-[#f0fdf9] transition-colors"
            >
              Back
            </button>
            <button
              onClick={handlePayNow}
              disabled={loadingPayment}
              className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#00BD7F] to-[#00B5E2] text-white font-semibold text-sm
                hover:opacity-90 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingPayment ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                  Processing…
                </span>
              ) : 'Pay Now'}
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default SubscriptionDialog;
